import { routeOptions } from "../routeOptions.js";

// Route selection service that integrates routeOptions with wayfinder data
export class RouteSelectionService {
  constructor() {
    this.routeOptions = routeOptions;
    this.routeCache = new Map(); // Cache for performance
  }

  // Find the best route based on wayfinder data and current time
  findBestRoute(wayfinderData) {
    if (!wayfinderData) {
      console.log("🚫 [ROUTE SELECTION] No wayfinder data provided");
      return null;
    }

    console.log("🔍 [ROUTE SELECTION] Finding best route for:", {
      assemblyPoint: wayfinderData.assemblyPoint,
      assemblyCoordinates: wayfinderData.assemblyCoordinates,
    });

    const cacheKey = `${
      wayfinderData.assemblyPoint
    }-${wayfinderData.assemblyCoordinates?.join(",")}`;

    // Check cache first
    if (this.routeCache.has(cacheKey)) {
      console.log("✅ [ROUTE SELECTION] Route found in cache:", cacheKey);
      return this.routeCache.get(cacheKey);
    }

    // Find matching routes by assembly point or coordinates
    const matchingRoutes = this.findMatchingRoutes(wayfinderData);
    console.log(
      `📋 [ROUTE SELECTION] Found ${matchingRoutes.length} matching routes`
    );

    if (matchingRoutes.length === 0) {
      console.log("❌ [ROUTE SELECTION] No matching routes found");
      return null;
    }

    // Select route based on time constraints and confirmation number distribution
    // Use BIB number as confirmation number for distribution
    const confirmationNumber =
      wayfinderData.bibNumber || wayfinderData.confirmationNumber;
    const selectedRoute = this.selectRouteByTime(
      matchingRoutes,
      confirmationNumber
    );
    console.log("🎯 [ROUTE SELECTION] Selected route:", {
      assemblyPoint: selectedRoute.assemblyPoint,
      isBefore6: selectedRoute.isBefore6,
      viaPointsCount: selectedRoute.viaPoints?.length || 0,
    });

    // Cache the result
    this.routeCache.set(cacheKey, selectedRoute);
    console.log("💾 [ROUTE SELECTION] Route cached with key:", cacheKey);

    return selectedRoute;
  }

  // Find routes that match the wayfinder data - DIRECT MATCHING ONLY
  findMatchingRoutes(wayfinderData) {
    const matchingRoutes = [];

    console.log(
      "🔍 [ROUTE MATCHING] Looking for EXACT match with wayfinder data"
    );

    // PRIORITY 1: Exact assembly point name match
    for (const route of this.routeOptions) {
      if (route.assemblyPoint === wayfinderData.assemblyPoint) {
        console.log(
          `✅ [ROUTE MATCHING] EXACT match by name: ${route.assemblyPoint}`
        );
        matchingRoutes.push(route);
        break; // Stop here - we found the exact match
      }
    }

    // PRIORITY 2: If no name match, try coordinates (but be more strict)
    if (matchingRoutes.length === 0) {
      console.log("🔍 [ROUTE MATCHING] No name match, trying coordinates...");
      for (const route of this.routeOptions) {
        if (
          this.coordinatesMatch(
            route.assemblyCoordinates,
            wayfinderData.assemblyCoordinates
          )
        ) {
          console.log(
            `✅ [ROUTE MATCHING] Coordinate match: ${route.assemblyPoint}`
          );
          matchingRoutes.push(route);
          break; // Stop after first coordinate match
        }
      }
    }

    if (matchingRoutes.length === 0) {
      console.log(
        "❌ [ROUTE MATCHING] NO MATCHES FOUND - check assembly point name/coordinates"
      );
    }

    console.log(
      `📊 [ROUTE MATCHING] Total matching routes: ${matchingRoutes.length}`
    );
    return matchingRoutes;
  }

  // Distribute users across routes based on confirmation number to avoid congestion
  distributeByConfirmationNumber(routes, confirmationNumber) {
    if (!confirmationNumber || routes.length <= 1) {
      return routes[0];
    }

    // Convert confirmation number to a hash for consistent distribution
    const hash = this.hashConfirmationNumber(confirmationNumber);

    // Use modulo to distribute evenly across available routes
    const routeIndex = hash % routes.length;
    const selectedRoute = routes[routeIndex];

    console.log(
      `🎲 [DISTRIBUTION] Confirmation ${confirmationNumber} → Hash ${hash} → Route ${
        routeIndex + 1
      }/${routes.length}: ${selectedRoute.assemblyPoint}`
    );

    return selectedRoute;
  }

  // Simple hash function for confirmation numbers
  hashConfirmationNumber(confirmationNumber) {
    let hash = 0;
    const str = confirmationNumber.toString();

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash);
  }

  // Check if two coordinate pairs match (with tolerance)
  coordinatesMatch(coord1, coord2, tolerance = 0.001) {
    if (!coord1 || !coord2 || coord1.length !== 2 || coord2.length !== 2) {
      return false;
    }

    const latDiff = Math.abs(coord1[0] - coord2[0]);
    const lngDiff = Math.abs(coord1[1] - coord2[1]);

    // The tolerance is in degrees, so we can use the raw differences
    return latDiff <= tolerance && lngDiff <= tolerance;
  }

  // Select route based on time constraints and confirmation number distribution
  selectRouteByTime(matchingRoutes, confirmationNumber = null) {
    if (matchingRoutes.length === 1) {
      // Only one route - use it regardless of time
      console.log(
        `🎯 [TIME SELECTION] Single route found: ${matchingRoutes[0].assemblyPoint}`
      );
      return matchingRoutes[0];
    }

    const now = new Date();
    const currentHour = now.getHours();
    const isBefore6AM = currentHour < 6;

    console.log(
      `⏰ [TIME SELECTION] Current time: ${currentHour}:00, isBefore6AM: ${isBefore6AM}`
    );

    // Filter routes by time constraints first
    const timeAppropriateRoutes = matchingRoutes.filter((route) => {
      if (route.isBefore6 === null) return true;
      return route.isBefore6 === isBefore6AM;
    });

    if (timeAppropriateRoutes.length === 0) {
      // No time-appropriate routes, use fallback
      console.log(
        `⚠️ [TIME SELECTION] No time-appropriate routes, using fallback: ${matchingRoutes[0].assemblyPoint}`
      );
      return matchingRoutes[0];
    }

    if (timeAppropriateRoutes.length === 1) {
      // Only one time-appropriate route
      console.log(
        `✅ [TIME SELECTION] Single time-appropriate route: ${timeAppropriateRoutes[0].assemblyPoint}`
      );
      return timeAppropriateRoutes[0];
    }

    // Multiple time-appropriate routes available - use confirmation number for distribution
    if (confirmationNumber && timeAppropriateRoutes.length > 1) {
      const selectedRoute = this.distributeByConfirmationNumber(
        timeAppropriateRoutes,
        confirmationNumber
      );
      console.log(
        `🎲 [TIME SELECTION] Distributed by confirmation number: ${selectedRoute.assemblyPoint}`
      );
      return selectedRoute;
    }

    // Fallback: return first time-appropriate route
    console.log(
      `⚠️ [TIME SELECTION] No confirmation number for distribution, using first: ${timeAppropriateRoutes[0].assemblyPoint}`
    );
    return timeAppropriateRoutes[0];
  }

  // Get route information for display
  getRouteInfo(route) {
    if (!route) {
      return null;
    }

    return {
      assemblyPoint: route.assemblyPoint,
      isBefore6: route.isBefore6,
      viaPoints: route.viaPoints || route.coordinates || [],
      pointCount: route.point_count,
      timeRestriction: this.getTimeRestrictionText(route.isBefore6),
      isCurrentlyAvailable: this.isRouteCurrentlyAvailable(route),
    };
  }

  // Get human-readable time restriction text
  getTimeRestrictionText(isBefore6) {
    if (isBefore6 === null) {
      return "Available anytime";
    }
    if (isBefore6 === true) {
      return "Available before 6:00 AM only";
    }
    if (isBefore6 === false) {
      return "Available after 6:00 AM only";
    }
    return "Time restriction unknown";
  }

  // Check if route is currently available
  isRouteCurrentlyAvailable(route) {
    if (route.isBefore6 === null) {
      return true;
    }

    const now = new Date();
    const currentHour = now.getHours();

    if (route.isBefore6 === true) {
      return currentHour < 6;
    }

    if (route.isBefore6 === false) {
      return currentHour >= 6;
    }

    return false;
  }

  // Find nearest via point to user location
  findNearestViaPoint(route, userLocation) {
    if (!route || !userLocation || !route.viaPoints) {
      console.log("🚫 [VIA POINT] Missing data:", {
        hasRoute: !!route,
        hasUserLocation: !!userLocation,
        hasViaPoints: !!route?.viaPoints,
      });
      return null;
    }

    console.log(
      `🔍 [VIA POINT] Finding nearest via point from ${route.viaPoints.length} options`
    );
    console.log(`📍 [VIA POINT] User location: [${userLocation.join(", ")}]`);

    let nearestPoint = null;
    let minDistance = Infinity;

    for (let i = 0; i < route.viaPoints.length; i++) {
      const viaPoint = route.viaPoints[i];
      const distance = this.calculateDistance(
        userLocation[1], // lat
        userLocation[0], // lng
        viaPoint.latitude,
        viaPoint.longitude
      );

      console.log(
        `  📍 Via point ${i + 1}: [${viaPoint.latitude}, ${
          viaPoint.longitude
        }] - Distance: ${distance.toFixed(3)}km`
      );

      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = viaPoint;
        console.log(`    🎯 New nearest point found!`);
      }
    }

    // If no via points or all distances are infinite, return null
    if (!nearestPoint || minDistance === Infinity) {
      console.log("❌ [VIA POINT] No valid via points found");
      return null;
    }

    console.log(
      `✅ [VIA POINT] Nearest via point: [${nearestPoint.latitude}, ${
        nearestPoint.longitude
      }] at ${minDistance.toFixed(3)}km`
    );

    // Ensure we always return a valid distance (minimum 0.001km if exactly at point)
    const finalDistance = minDistance === 0 ? 0.001 : minDistance;

    return {
      point: nearestPoint,
      distance: finalDistance,
    };
  }

  // Calculate distance between two points (Haversine formula)
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // Get all available routes for a specific assembly point
  getAllRoutesForAssembly(assemblyPoint) {
    return this.routeOptions.filter(
      (route) => route.assemblyPoint === assemblyPoint
    );
  }

  // Clear route cache (useful for testing or when routes change)
  clearCache() {
    this.routeCache.clear();
  }

  // Get cache statistics
  getCacheStats() {
    return {
      cacheSize: this.routeCache.size,
      cachedRoutes: Array.from(this.routeCache.keys()),
    };
  }
}

// Export singleton instance
export const routeSelectionService = new RouteSelectionService();
